/**
 * /settings/security — the user's security controls.
 *
 * Sections:
 *   1. Change password     (user.updatePassword)
 *   2. Two-factor (TOTP)   (user.createTOTP → user.verifyTOTP →
 *                           user.createBackupCode for show-once codes)
 *      Disable: user.disableTOTP.
 *   3. Active sessions     (user.getSessions → revoke others / single)
 *
 * The Security page uses the existing settings layout (two-pane via
 * `/_app/settings.tsx`). We render into the Island body with
 * section cards.
 */

import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { OTPField, OTPFieldInput } from '@/components/ui/otp-field';
import { Spinner } from '@/components/ui/spinner';
import {
  changePassword,
  disableTotp,
  enrollTotp,
  revokeOtherSessions,
  verifyTotpEnrollment,
} from '@/lib/auth';
import { useClerk, useSession, useUser } from '@clerk/react';
import type { SessionWithActivitiesResource } from '@clerk/shared/types';
import { ArrowsClockwise, Check, Copy, Eye, EyeSlash, Trash } from '@phosphor-icons/react';
import { createFileRoute } from '@tanstack/react-router';
import QRCode from 'qrcode';
import { type FormEvent, type ReactElement, useCallback, useEffect, useRef, useState } from 'react';

export const Route = createFileRoute('/_app/settings/security')({
  component: SecurityPage,
});

function SecurityPage(): ReactElement {
  return (
    <div className="flex flex-col gap-8">
      <ChangePasswordSection />
      <SectionSeparator />
      <TotpSection />
      <SectionSeparator />
      <SessionsSection />
    </div>
  );
}

function SectionSeparator(): ReactElement {
  return <div className="h-px w-full bg-[var(--color-line)]" />;
}

function SectionHeader({
  title,
  description,
}: {
  title: string;
  description: string;
}): ReactElement {
  return (
    <header className="space-y-1">
      <h2 className="text-base font-semibold text-[var(--color-ink)]">{title}</h2>
      <p className="text-sm text-[var(--color-mute)]">{description}</p>
    </header>
  );
}

// -----------------------------------------------------------------
// 1. Change password
// -----------------------------------------------------------------

function ChangePasswordSection(): ReactElement {
  const { user, isLoaded } = useUser();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [signOutOthers, setSignOutOthers] = useState(true);
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [success, setSuccess] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isLoaded || !user) return;

    setFieldErrors({});
    setFormError(undefined);
    setSuccess(false);

    if (next !== confirm) {
      setFieldErrors({ confirm: 'Passwords do not match.' });
      return;
    }

    setSubmitting(true);

    const result = await changePassword(user, {
      currentPassword: user.passwordEnabled ? current : undefined,
      newPassword: next,
      signOutOtherSessions: signOutOthers,
    });

    if (result.kind === 'error') {
      setFieldErrors(result.fieldErrors);
      setFormError(
        result.fieldErrors.password || result.fieldErrors.currentPassword
          ? undefined
          : result.formError,
      );
      setSubmitting(false);
      return;
    }

    setCurrent('');
    setNext('');
    setConfirm('');
    setSuccess(true);
    setSubmitting(false);
  }

  return (
    <section className="flex flex-col gap-4">
      <SectionHeader
        title="Change password"
        description="Use at least 8 characters. Changing your password optionally signs you out of every other session."
      />
      <form className="space-y-4 max-w-md" onSubmit={onSubmit} noValidate>
        {user?.passwordEnabled && (
          <Field>
            <FieldLabel htmlFor="current-password">Current password</FieldLabel>
            <Input
              id="current-password"
              type={show ? 'text' : 'password'}
              autoComplete="current-password"
              value={current}
              onChange={(event) => setCurrent((event.target as HTMLInputElement).value)}
              aria-invalid={fieldErrors.currentPassword ? true : undefined}
              required
            />
            {fieldErrors.currentPassword && <FieldError>{fieldErrors.currentPassword}</FieldError>}
          </Field>
        )}
        <Field>
          <FieldLabel htmlFor="new-password">New password</FieldLabel>
          <div className="relative w-full">
            <Input
              id="new-password"
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              minLength={8}
              value={next}
              onChange={(event) => setNext((event.target as HTMLInputElement).value)}
              aria-invalid={fieldErrors.password ? true : undefined}
              required
            />
            <button
              type="button"
              aria-label={show ? 'Hide password' : 'Show password'}
              onClick={() => setShow((prev) => !prev)}
              className="absolute inset-y-0 right-2 flex items-center text-[var(--color-mute)] hover:text-[var(--color-ink)]"
            >
              {show ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {fieldErrors.password && <FieldError>{fieldErrors.password}</FieldError>}
        </Field>
        <Field>
          <FieldLabel htmlFor="confirm-password">Confirm new password</FieldLabel>
          <Input
            id="confirm-password"
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm((event.target as HTMLInputElement).value)}
            aria-invalid={fieldErrors.confirm ? true : undefined}
            required
          />
          {fieldErrors.confirm && <FieldError>{fieldErrors.confirm}</FieldError>}
        </Field>
        <label className="flex items-center gap-2 text-sm text-[var(--color-mute)]">
          <input
            type="checkbox"
            checked={signOutOthers}
            onChange={(event) => setSignOutOthers(event.target.checked)}
            className="size-4 rounded border border-[var(--color-line)] accent-[var(--color-accent-brand)]"
          />
          Sign out of other sessions
        </label>
        {formError && (
          <div
            className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
            role="alert"
          >
            {formError}
          </div>
        )}
        {success && (
          <output className="rounded-md border border-[var(--color-success)]/30 bg-[var(--color-success)]/10 px-3 py-2 text-sm text-[var(--color-success)]">
            Password updated.
          </output>
        )}
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Updating…' : 'Update password'}
        </Button>
      </form>
    </section>
  );
}

// -----------------------------------------------------------------
// 2. TOTP 2FA
// -----------------------------------------------------------------

function TotpSection(): ReactElement {
  const { user, isLoaded } = useUser();
  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState<string | undefined>(undefined);
  const [otpauthUri, setOtpauthUri] = useState<string | undefined>(undefined);
  const [secret, setSecret] = useState<string | undefined>(undefined);
  const [qrDataUri, setQrDataUri] = useState<string | undefined>(undefined);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | undefined>(undefined);
  const [backupCodes, setBackupCodes] = useState<string[] | undefined>(undefined);
  const [disabling, setDisabling] = useState(false);
  const attemptingRef = useRef(false);

  const totpEnabled = user?.totpEnabled ?? false;

  useEffect(() => {
    if (!otpauthUri) return;
    let cancelled = false;
    QRCode.toDataURL(otpauthUri, { margin: 1, width: 192 })
      .then((url) => {
        if (!cancelled) setQrDataUri(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUri(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [otpauthUri]);

  async function onEnroll() {
    if (!isLoaded || !user) return;
    setEnrollError(undefined);
    setEnrolling(true);
    setBackupCodes(undefined);

    const result = await enrollTotp(user);

    if (result.kind === 'error') {
      setEnrollError(result.formError ?? 'Could not start TOTP enrollment.');
      setEnrolling(false);
      return;
    }
    setOtpauthUri(result.uri);
    setSecret(result.secret);
  }

  const attemptVerify = useCallback(
    async (value: string) => {
      if (!isLoaded || !user) return;
      if (attemptingRef.current) return;
      attemptingRef.current = true;
      setVerifying(true);
      setVerifyError(undefined);

      const result = await verifyTotpEnrollment(user, value);

      if (result.kind === 'error') {
        setVerifyError(result.fieldErrors.code ?? result.formError ?? 'Invalid code.');
        setCode('');
        attemptingRef.current = false;
        setVerifying(false);
        return;
      }

      setBackupCodes(result.backupCodes);
      setOtpauthUri(undefined);
      setSecret(undefined);
      setQrDataUri(undefined);
      setCode('');
      attemptingRef.current = false;
      setVerifying(false);
      setEnrolling(false);
    },
    [isLoaded, user],
  );

  function onChangeCode(value: string) {
    setCode(value);
    if (value.length === 6) {
      void attemptVerify(value);
    }
  }

  async function onDisable() {
    if (!isLoaded || !user) return;
    if (!window.confirm('Disable two-factor authentication?')) return;
    setDisabling(true);
    const result = await disableTotp(user);
    if (result.kind === 'error') {
      setEnrollError(result.formError ?? 'Could not disable TOTP.');
    } else {
      setBackupCodes(undefined);
      setEnrolling(false);
    }
    setDisabling(false);
  }

  if (!isLoaded || !user) {
    return (
      <section className="flex flex-col gap-3">
        <SectionHeader title="Two-factor authentication" description="Loading…" />
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-4">
      <SectionHeader
        title="Two-factor authentication"
        description="Require a code from an authenticator app in addition to your password."
      />
      {totpEnabled && !enrolling ? (
        <div className="flex items-center gap-3 rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)] px-4 py-3">
          <Check size={18} className="text-[var(--color-success)]" aria-hidden="true" />
          <div className="flex-1 text-sm text-[var(--color-ink)]">
            TOTP is enabled on your account.
          </div>
          <Button variant="destructive-outline" onClick={onDisable} disabled={disabling}>
            {disabling ? 'Disabling…' : 'Disable 2FA'}
          </Button>
        </div>
      ) : !otpauthUri ? (
        <div className="flex items-center gap-3">
          <Button onClick={onEnroll} disabled={enrolling}>
            {enrolling ? 'Starting…' : 'Enable 2FA'}
          </Button>
          {enrollError && (
            <span className="text-sm text-[var(--color-danger)]" role="alert">
              {enrollError}
            </span>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4 rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)] p-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-start">
            <div className="flex size-48 items-center justify-center rounded-md border border-[var(--color-line)] bg-white">
              {qrDataUri ? (
                <img src={qrDataUri} alt="TOTP QR code" className="size-44" />
              ) : (
                <Spinner className="size-5 text-[var(--color-mute)]" />
              )}
            </div>
            <div className="flex-1 space-y-2 text-sm text-[var(--color-mute)]">
              <p>
                Scan the QR code with your authenticator app (1Password, Authy, Google
                Authenticator, etc.), then enter the 6-digit code below to verify.
              </p>
              {secret && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs uppercase tracking-wide text-[var(--color-mute)]">
                    Or enter this secret manually
                  </span>
                  <code className="rounded-sm bg-[var(--color-paper)] px-2 py-1 font-mono text-xs text-[var(--color-ink)] break-all">
                    {secret}
                  </code>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col items-start gap-2">
            <span className="text-sm font-medium text-[var(--color-ink)]">Verification code</span>
            <OTPField length={6} value={code} onValueChange={onChangeCode} disabled={verifying}>
              {Array.from({ length: 6 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length OTP slots
                <OTPFieldInput key={i} />
              ))}
            </OTPField>
            {verifyError && (
              <p className="text-sm text-[var(--color-danger)]" role="alert">
                {verifyError}
              </p>
            )}
          </div>
        </div>
      )}
      {backupCodes && (
        <BackupCodesPanel codes={backupCodes} onDone={() => setBackupCodes(undefined)} />
      )}
    </section>
  );
}

function BackupCodesPanel({
  codes,
  onDone,
}: {
  codes: string[];
  onDone: () => void;
}): ReactElement {
  const [copied, setCopied] = useState(false);

  async function copyAll() {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore clipboard errors */
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-md border border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10 p-4">
      <header className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-[var(--color-ink)]">Save your backup codes</h3>
        <p className="text-xs text-[var(--color-mute)]">
          Each code works once. Store them somewhere safe — we won't show them again.
        </p>
      </header>
      <ul className="grid grid-cols-2 gap-2 font-mono text-sm text-[var(--color-ink)]">
        {codes.map((code) => (
          <li key={code} className="rounded-sm bg-[var(--color-paper)] px-2 py-1">
            {code}
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={copyAll}>
          {copied ? (
            <>
              <Check aria-hidden="true" />
              Copied
            </>
          ) : (
            <>
              <Copy aria-hidden="true" />
              Copy all
            </>
          )}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          I've saved them
        </Button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// 3. Active sessions
// -----------------------------------------------------------------

function SessionsSection(): ReactElement {
  const { user, isLoaded } = useUser();
  const { session } = useSession();
  const clerk = useClerk();
  const [sessions, setSessions] = useState<SessionWithActivitiesResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const refresh = useCallback(async () => {
    if (!isLoaded || !user) return;
    setLoading(true);
    setError(undefined);
    try {
      const result = await user.getSessions();
      setSessions(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load sessions.');
    } finally {
      setLoading(false);
    }
  }, [isLoaded, user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onRevokeOthers() {
    if (!user || !session) return;
    if (!window.confirm('Sign out of every other session?')) return;
    setWorking(true);
    setError(undefined);
    const result = await revokeOtherSessions(user, session.id);
    setWorking(false);
    if (result.kind === 'error') {
      setError(result.formError ?? 'Unable to revoke sessions.');
      return;
    }
    await refresh();
  }

  async function onRevokeOne(s: SessionWithActivitiesResource) {
    if (session && s.id === session.id) {
      if (!window.confirm('Revoking your current session will sign you out. Continue?')) return;
    }
    setWorking(true);
    setError(undefined);
    try {
      await s.revoke();
      if (session && s.id === session.id) {
        await clerk.signOut();
        return;
      }
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to revoke session.');
    } finally {
      setWorking(false);
    }
  }

  const otherCount = sessions.filter((s) => s.status === 'active' && s.id !== session?.id).length;

  return (
    <section className="flex flex-col gap-4">
      <SectionHeader
        title="Active sessions"
        description="Sign out of devices you don't recognise."
      />
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={refresh} disabled={loading || working}>
          <ArrowsClockwise aria-hidden="true" />
          Refresh
        </Button>
        <Button
          variant="destructive-outline"
          onClick={onRevokeOthers}
          disabled={working || otherCount === 0}
        >
          Sign out other sessions{otherCount > 0 ? ` (${otherCount})` : ''}
        </Button>
      </div>
      {error && (
        <div
          className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
          role="alert"
        >
          {error}
        </div>
      )}
      <ul className="divide-y divide-[var(--color-line)] rounded-md border border-[var(--color-line)]">
        {loading && (
          <li className="flex items-center justify-center p-6">
            <Spinner className="size-5 text-[var(--color-mute)]" />
          </li>
        )}
        {!loading &&
          sessions.map((s) => {
            const isCurrent = session ? s.id === session.id : false;
            const activity = s.latestActivity;
            const device = [activity?.browserName, activity?.deviceType]
              .filter(Boolean)
              .join(' · ');
            const location = [activity?.city, activity?.country].filter(Boolean).join(', ');
            return (
              <li key={s.id} className="flex items-center gap-4 px-4 py-3">
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-[var(--color-ink)]">
                      {device || 'Unknown device'}
                    </span>
                    {isCurrent && (
                      <span className="rounded-sm bg-[var(--color-accent-brand)]/10 px-1.5 py-0.5 text-xs font-medium text-[var(--color-accent-brand)]">
                        This session
                      </span>
                    )}
                  </div>
                  <div className="truncate text-xs text-[var(--color-mute)]">
                    {location || activity?.ipAddress || '—'} · last active{' '}
                    {s.lastActiveAt.toLocaleString()}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Revoke session"
                  onClick={() => onRevokeOne(s)}
                  disabled={working}
                >
                  <Trash aria-hidden="true" />
                </Button>
              </li>
            );
          })}
      </ul>
    </section>
  );
}
